import React from "react";
import { CreditReturn, CreditReturnItem } from "../../types/traceability";
import { kgFmt, lbsFmt, pcsFmt } from "../../shared/formatting";
import CreditReturnItemsTable from "../../components/traceability/CreditReturnItemsTable";

interface CreditReturnTraceabilityProps {
  lookup: string;
  loading: boolean;
  error: string | null;
  creditReturn: CreditReturn | null;
  creditReturnItems: CreditReturnItem[];
}

const dollarFmt = (v: number | null | undefined): string =>
  v == null ? "—" : `$${Number(v).toFixed(2)}`;

export default function CreditReturnTraceability({
  lookup,
  loading,
  error,
  creditReturn,
  creditReturnItems,
}: CreditReturnTraceabilityProps) {
  // Empty state
  if (!creditReturn && !loading && !error) {
    return (
      <div className="px-4">
        <div className="card-custom bg-white p-6">
          <p className="text-gray-600">
            Enter a lot number to search for a credit return.
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !creditReturn) {
    return (
      <div className="px-4">
        <div className="card-custom bg-white p-6">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Credit Return Summary Card */}
      {creditReturn && (
        <div className="px-4">
          <div className="card-custom bg-white p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="sm:flex-auto w-full">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-semibold leading-6 text-gray-900">
                    {creditReturn.name}
                  </h2>
                  <span
                    className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                      creditReturn.status === 1
                        ? "bg-green-50 text-green-700 ring-green-600/20"
                        : "bg-yellow-50 text-yellow-700 ring-yellow-600/20"
                    }`}
                  >
                    {creditReturn.status === 1 ? "Closed" : "Open"}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Customer: {creditReturn.customer_name}
                </p>
                {creditReturn.notes && (
                  <p className="text-sm text-gray-500 mt-1">
                    Notes: {creditReturn.notes}
                  </p>
                )}
              </div>
              <div className="shrink-0">
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  onClick={() =>
                    window.open(`/credit-returns/${creditReturn.id}`, "_blank")
                  }
                >
                  View Credit Return
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-600 mb-1">
                  Total Weight (kg)
                </div>
                <div className="text-xl font-semibold text-gray-900">
                  {kgFmt(creditReturn.total_weight_kg)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-600 mb-1">
                  Total Weight (lbs)
                </div>
                <div className="text-xl font-semibold text-gray-900">
                  {lbsFmt(creditReturn.total_weight_lbs)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-600 mb-1">
                  Total Pieces
                </div>
                <div className="text-xl font-semibold text-gray-900">
                  {pcsFmt(creditReturn.total_pieces)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-600 mb-1">
                  Total Cost
                </div>
                <div className="text-xl font-semibold text-gray-900">
                  {dollarFmt(creditReturn.total_cost)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Items Table */}
      {creditReturn && (
        <section className="px-4">
          <div className="card-custom p-4">
            <div className="sm:flex-auto w-full mb-4">
              <h3 className="text-base font-semibold leading-6 text-gray-900">
                Credit Return Items
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                All items in credit return for lot number {lookup}
              </p>
            </div>
            <CreditReturnItemsTable items={creditReturnItems} />
          </div>
        </section>
      )}
    </>
  );
}
